/**
 * W1-11b (app-shell-ui): the transport in the top bar (DESIGN.md §4.4): go to start, play / pause, "go back to the
 * start point on stop" toggle, the playhead readout as a beat (the settings' beat display: `12:3/4`, `12 + 3/4` or
 * bar and beat under the chart's meter) and seconds,
 * and the rate. The readout is ref-driven at 15 Hz (no React render per frame); clicking it turns it into a field
 * that takes any beat-field syntax (`37:1/4`, `+4`, `23.5s`, `@1:23.5`, `m2`) and seeks there.
 *
 * W3-01 (UX-008): after a pause or a scrub the playhead rarely sits on the grid. A beat off the snap grid is shown as
 * the nearest snap with a "~" mark ("~5:3/4") at the Guided level, and at both levels when its exact fraction would
 * have a denominator above 16 ("10:869/1920"); the exact beat is in the tooltip and the accessible name.
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';
import { formatSec, parseBeat } from '../../core/time/beatSyntax';
import { IconButton } from '../../ui/IconButton';
import { Menu } from '../../ui/Menu';
import type { MenuItem } from '../../ui/types';
import type { AppServices } from '../registry/types';
import { displayBeat, formatRate, playheadSec, RATE_PRESETS, seekSec, tempoOf } from './playback';
import { useEditorState, useSettingsValue, useStore, useText, useTransportState } from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

/** Readout refresh rate while playing (DESIGN/ARCHITECTURE: 15 Hz). */
export const READOUT_INTERVAL_MS = 1000 / 15;

/** The beat shown for a time: exact when paused, floored to the snap grid while playing (no flicker). */
export function readoutBeat(t: number, playing: boolean, snapDiv: number, tempo: ReturnType<typeof tempoOf>): Beat {
  const exact = tempo.beatAt(Math.max(0, t), 1920);
  return playing ? Beats.floorTo(exact, Math.max(1, snapDiv)) : exact;
}

/** Largest denominator the readout shows as it is; finer beats are shown rounded to the snap. */
export const READOUT_MAX_DENOMINATOR = 16;

export interface ReadoutValue {
  /** The beat to show. */
  readonly beat: Beat;
  /** The exact beat at the playhead (1/1920 grid). */
  readonly exact: Beat;
  /** `beat` is the nearest snap, not the exact beat (shown with "~"). */
  readonly approx: boolean;
}

/**
 * The readout's beat (UX-008): floored to the snap grid while playing; when paused the exact beat, unless it is off
 * the snap grid at the Guided level or needs a denominator above 16: then the nearest snap, marked approximate.
 */
export function readoutValue(
  t: number,
  playing: boolean,
  snapDiv: number,
  tempo: ReturnType<typeof tempoOf>,
  level: 'guided' | 'full',
): ReadoutValue {
  const div = Math.max(1, snapDiv);
  const exact = tempo.beatAt(Math.max(0, t), 1920);
  if (playing) return { beat: Beats.floorTo(exact, div), exact, approx: false };
  const snapped = Beats.snap(exact, div);
  if (Beats.eq(snapped, exact)) return { beat: exact, exact, approx: false };
  if (level === 'guided' || exact.d > READOUT_MAX_DENOMINATOR) return { beat: snapped, exact, approx: true };
  return { beat: exact, exact, approx: false };
}

export function TransportBar(props: { readonly services: AppServices }) {
  const { services } = props;
  const text = useText(services);
  const settings = useSettingsValue(services);
  const transport = useTransportState(services.audio);
  const store = useStore(services);
  const snapDiv = useEditorState(services, (st) => st.snap.div);
  const returnOnStop = useEditorState(services, (st) => st.returnOnStop);
  const rate = useEditorState(services, (st) => st.rate);
  const beatRef = useRef<HTMLSpanElement>(null);
  const secRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const style = settings.editing.beatDisplay;

  const level = settings.general.level;
  const update = useCallback((): void => {
    const t = playheadSec(services);
    const value =
      store === null
        ? null
        : readoutValue(t, services.audio.transport.state.playing, snapDiv, tempoOf(store), level);
    const shown = value === null || store === null ? '-' : displayBeat(value.beat, style, store, text.tr);
    const exactText =
      value === null || store === null || !value.approx ? null : displayBeat(value.exact, style, store, text.tr);
    const beatText = exactText === null ? shown : text.t(m.readoutApprox, { beat: shown });
    const secText = formatSec(t);
    if (beatRef.current !== null && beatRef.current.textContent !== beatText) beatRef.current.textContent = beatText;
    if (secRef.current !== null && secRef.current.textContent !== secText) secRef.current.textContent = secText;
    const button = buttonRef.current;
    if (button !== null) {
      const exactBeat = exactText ?? shown;
      button.setAttribute('aria-label', text.t(m.timeReadout, { beat: exactBeat, time: secText }));
      const title = exactText === null ? '' : text.t(m.readoutExact, { beat: exactText });
      if ((button.getAttribute('title') ?? '') !== title) {
        if (title === '') button.removeAttribute('title');
        else button.setAttribute('title', title);
      }
    }
  }, [services, store, snapDiv, style, text, level]);

  useEffect(() => {
    update();
    const timer = setInterval(update, transport.playing ? READOUT_INTERVAL_MS : 250);
    const off = services.audio.transport.onChange(update);
    return () => {
      clearInterval(timer);
      off();
    };
  }, [update, transport.playing, services]);

  // The readout is mounted again after typing a position: fill it at once.
  useEffect(() => {
    if (!editing) update();
  }, [editing, update]);

  const commit = (): void => {
    if (store === null) {
      setEditing(false);
      return;
    }
    const tempo = tempoOf(store);
    const markers = [...store.doc.markers.values()].sort((a, b) => Beats.cmp(a.t, b.t));
    const result = parseBeat(draft, {
      snapDiv,
      syntax: settings.editing.beatSyntax,
      current: tempo.beatAt(playheadSec(services)),
      tempo,
      marker: (name) => markers.find((x) => x.name === name)?.t ?? markers[Number(name) - 1]?.t,
    });
    if (!result.ok) {
      setInvalid(true);
      return;
    }
    seekSec(services, tempo.secAt(result.beat));
    setEditing(false);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setEditing(false);
    }
  };

  const rateItems: MenuItem[] = [
    ...RATE_PRESETS.map((r): MenuItem => ({
      kind: 'action',
      label: text.t(m.rateOption, { rate: formatRate(r) }),
      ...(Math.abs(r - rate) < 1e-9 ? { icon: 'Check' } : {}),
      onSelect: () => {
        void services.registry.runAny('playback.setRate', r, 'menu');
      },
    })),
    { kind: 'separator' },
    { kind: 'command', command: 'playback.rateDown' },
    { kind: 'command', command: 'playback.rateUp' },
  ];

  return (
    <div className={s.transport} role="group" aria-label={text.t(m.transportLabel)}>
      <IconButton icon="SkipBack" label={{ key: m.cmdSeekStart }} command="playback.seekStart" />
      <IconButton
        icon={transport.playing ? 'Pause' : 'Play'}
        label={{ key: transport.playing ? m.pause : m.play }}
        command="playback.toggle"
        helpId="transport.play"
      />
      <IconButton
        icon="Repeat"
        label={{ key: m.cmdReturnOnStop }}
        command="playback.returnOnStop"
        pressed={returnOnStop}
      />
      {editing ? (
        <input
          className={s.timeInput}
          aria-label={text.t(m.timeInputLabel)}
          aria-invalid={invalid ? true : undefined}
          title={invalid ? text.t(m.timeInvalid) : undefined}
          placeholder={text.t(m.timeInputPlaceholder)}
          value={draft}
          autoFocus
          onChange={(e) => {
            setDraft(e.target.value);
            setInvalid(false);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setEditing(false);
          }}
        />
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className={s.timeReadout}
          disabled={store === null}
          onClick={() => {
            setDraft('');
            setInvalid(false);
            setEditing(true);
          }}
        >
          <span ref={beatRef} className={s.timeBeat} />
          <span ref={secRef} className={s.timeSec} />
        </button>
      )}
      <Menu
        label={text.t(m.argRate)}
        trigger={
          <button
            type="button"
            className={s.rateButton}
            aria-label={text.t(m.rateLabel, { rate: formatRate(rate) })}
            title={services.audio.music?.streaming === true ? text.t(m.rateStreaming) : undefined}
          >
            {text.t(m.rateOption, { rate: formatRate(rate) })}
          </button>
        }
        items={rateItems}
      />
    </div>
  );
}
